import { ApiError, type ApiErrorCode } from '@/types/api';
import { csrfStore } from './csrfStore';

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

type AuthListener = () => void;
const authListeners = new Set<AuthListener>();

export function onUnauthorized(fn: AuthListener): () => void {
  authListeners.add(fn);
  return () => {
    authListeners.delete(fn);
  };
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  skipCsrf?: boolean;
  silentUnauthorized?: boolean;
}

function statusToCode(status: number): ApiErrorCode {
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 409) return 'conflict';
  if (status === 422) return 'validation';
  if (status === 404) return 'not_found';
  if (status === 429) return 'rate_limited';
  if (status >= 500) return 'server_error';
  return 'unknown';
}

export async function apiRequest<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = (opts.method ?? 'GET').toUpperCase();
  const headers: Record<string, string> = { Accept: 'application/json' };

  let body: BodyInit | undefined;
  if (opts.body !== undefined) {
    if (opts.body instanceof FormData) {
      body = opts.body;
    } else {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(opts.body);
    }
  }

  if (!opts.skipCsrf && MUTATING.has(method)) {
    const tok = csrfStore.get();
    if (tok) headers['X-CSRF-Token'] = tok;
  }

  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: 'include',
      headers,
      body,
      signal: opts.signal,
    });
  } catch (err) {
    throw new ApiError(0, 'network_error', (err as Error).message || 'Network error');
  }

  if (res.status === 204) return undefined as T;

  let data: unknown = null;
  const ct = res.headers.get('content-type') ?? '';
  if (ct.includes('application/json')) {
    try {
      data = await res.json();
    } catch {
      data = null;
    }
  } else {
    data = await res.text();
  }

  if (!res.ok) {
    const code = statusToCode(res.status);
    const message =
      data && typeof data === 'object' && 'message' in (data as object)
        ? String((data as { message: unknown }).message)
        : `Request failed (${res.status})`;
    const retryAfter = Number(res.headers.get('Retry-After')) || undefined;

    if (code === 'unauthorized' && !opts.silentUnauthorized) {
      authListeners.forEach((fn) => fn());
    }

    throw new ApiError(res.status, code, message, data, retryAfter);
  }

  return data as T;
}
